// Iter 395 (Renzo) — Comprobante de Egreso en Alegra al generar un TEF de colaboradores (1 egreso por nómina, total exacto).
import { useEffect, useMemo, useState } from 'react';
import { Landmark, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const hoyChile = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
export const egresoCfgInicial = () => ({ crear: true, cuenta_id: '', fecha: hoyChile() });

export function useEgresoAlegraCuentas(open) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    api.get('/tef/cuentas-colaboradores/egreso-alegra/cuentas')
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'No se pudieron cargar las cuentas contables de Alegra'))
      .finally(() => setLoading(false));
  }, [open, data]);
  return { data, loading };
}

export function describirEgresoAlegra(info) {
  if (!info) return null;
  if (info.estado === 'creado') return `Comprobante de Egreso N°${info.number || info.payment_id} creado en Alegra (${info.cuenta_nombre || ''})`;
  if (info.estado === 'anulado') return `Egreso N°${info.number || info.payment_id} anulado en Alegra`;
  return `Egreso en Alegra NO creado: ${info.error || 'error'} — reintenta desde el Historial`;
}

/** Bloque de campos: crear sí/no, cuenta contable (default 5037) y fecha. `value`/`onChange` controlados. */
export function EgresoAlegraNominaFields({ open, value, onChange, total, testidPrefix = 'egreso-alegra' }) {
  const { data, loading } = useEgresoAlegraCuentas(open);
  const cuentas = useMemo(() => data?.cuentas || [], [data]);
  const sugeridas = useMemo(() => new Set(data?.sugeridas || []), [data]);

  useEffect(() => {
    if (data?.default && !value.cuenta_id) onChange({ ...value, cuenta_id: data.default });
  }, [data, value, onChange]);

  const ordenadas = useMemo(() => [
    ...cuentas.filter((c) => sugeridas.has(c.id)).sort((a, b) => [...sugeridas].indexOf(a.id) - [...sugeridas].indexOf(b.id)),
    ...cuentas.filter((c) => !sugeridas.has(c.id)).sort((a, b) => a.name.localeCompare(b.name)),
  ], [cuentas, sugeridas]);

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2" data-testid={`${testidPrefix}-bloque`}>
      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
        <input type="checkbox" checked={!!value.crear} onChange={(e) => onChange({ ...value, crear: e.target.checked })} data-testid={`${testidPrefix}-crear`} />
        <Landmark className="h-4 w-4 text-sky-700" /> Crear Comprobante de Egreso en Alegra por el total del TEF{total ? ` ($${Math.round(total).toLocaleString('es-CL')})` : ''}
      </label>
      {value.crear && (
        <div className="grid gap-2 sm:grid-cols-[1fr_150px]">
          <div>
            <Label className="text-xs">Cuenta contable (contacto Sueldos · banco de la cuenta de cargo)</Label>
            {loading ? (
              <p className="flex items-center gap-1 text-xs text-muted-foreground h-9"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando plan de cuentas…</p>
            ) : (
              <Select value={value.cuenta_id || ''} onValueChange={(v) => onChange({ ...value, cuenta_id: v })}>
                <SelectTrigger className="h-9 text-xs bg-white" data-testid={`${testidPrefix}-cuenta`}><SelectValue placeholder="Elige la cuenta contable" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {ordenadas.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-xs">
                      {sugeridas.has(c.id) ? '★ ' : ''}{c.code ? `${c.code} · ` : ''}{c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs">Fecha del egreso</Label>
            <Input type="date" value={value.fecha || ''} onChange={(e) => onChange({ ...value, fecha: e.target.value })} className="h-9 text-xs bg-white" data-testid={`${testidPrefix}-fecha`} />
          </div>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">El TEF se genera igual aunque Alegra falle; en el Historial queda el estado del egreso con "Crear en Alegra" (reintento) y "Anular". Al subir la cartola, los traspasos que calcen 1 a 1 con las líneas del TEF se concilian en grupo automáticamente.</p>
    </div>
  );
}
